import { mixToMono, adaptiveThreshold } from "./audioUtils";

/**
 * Beat detection for the Riffed guessing game.
 *
 * Detects rhythmic beats (kicks, snares, strong pulses) by analyzing
 * energy peaks in low-frequency bands. Complements onset detection
 * which catches all note beginnings including melodic ones.
 */

export interface BeatResult {
  /** Timestamps (in seconds) of detected beats */
  beats: number[];
  /** Estimated BPM */
  bpm: number;
  /** Audio buffer duration in seconds */
  duration: number;
}

export interface BeatDetectorOptions {
  /** Minimum expected BPM (default: 60) */
  minBPM?: number;
  /** Maximum expected BPM (default: 200) */
  maxBPM?: number;
  /** Sensitivity — lower = more beats detected (default: 1.4) */
  sensitivity?: number;
}

export function detectBeats(
  audioBuffer: AudioBuffer,
  options: BeatDetectorOptions = {}
): BeatResult {
  const { minBPM = 60, maxBPM = 200, sensitivity = 1.4 } = options;

  const sampleRate = audioBuffer.sampleRate;
  const mono = mixToMono(audioBuffer);

  // Low-pass filter: keep only frequencies below ~200Hz (kick/bass)
  const filtered = lowPassFilter(mono, sampleRate, 200);

  // Compute energy envelope
  const windowSize = Math.round(sampleRate * 0.01); // 10ms windows
  const hopSize = Math.round(sampleRate * 0.005); // 5ms hop
  const envelope = computeEnergyEnvelope(filtered, windowSize, hopSize);

  // Find peaks in energy envelope
  const hopDuration = hopSize / sampleRate;
  const minGapSec = 60 / maxBPM; // minimum gap based on max BPM
  // Adaptive threshold
  const thresholdWindow = Math.round(0.5 / hopDuration); // 500ms window
  const thresholds = adaptiveThreshold(envelope, sensitivity, thresholdWindow);

  const beats: number[] = [];

  for (let i = 1; i < envelope.length - 1; i++) {
    // Must be a local peak above threshold
    if (
      envelope[i] > thresholds[i] &&
      envelope[i] > envelope[i - 1] &&
      envelope[i] >= envelope[i + 1]
    ) {
      const timeSec = i * hopDuration;

      // Enforce minimum gap
      if (beats.length === 0 || timeSec - beats[beats.length - 1] >= minGapSec) {
        beats.push(timeSec);
      }
    }
  }

  // Estimate BPM from beat intervals
  const bpm = estimateBPM(beats, minBPM, maxBPM);

  return { beats, bpm, duration: audioBuffer.duration };
}

/**
 * Given beat timestamps and a stage number (1-6),
 * returns the end timestamp for that stage's clip.
 */
export function getClipEndForBeatStage(
  beats: number[],
  stage: number,
  startSec: number,
  totalDuration: number
): number {
  const relevantBeats = beats.filter((t) => t >= startSec);

  if (relevantBeats.length === 0) {
    return Math.min(startSec + stage, totalDuration);
  }

  if (stage >= relevantBeats.length) {
    const lastBeat = relevantBeats[relevantBeats.length - 1];
    return Math.min(lastBeat + 0.5, totalDuration);
  }

  return relevantBeats[stage] - 0.02;
}

// --- Internal ---

function lowPassFilter(
  samples: Float32Array,
  sampleRate: number,
  cutoff: number
): Float32Array {
  // Simple one-pole low-pass filter
  const rc = 1 / (2 * Math.PI * cutoff);
  const dt = 1 / sampleRate;
  const alpha = dt / (rc + dt);

  const filtered = new Float32Array(samples.length);
  filtered[0] = samples[0];

  for (let i = 1; i < samples.length; i++) {
    filtered[i] = filtered[i - 1] + alpha * (samples[i] - filtered[i - 1]);
  }

  return filtered;
}

function computeEnergyEnvelope(
  samples: Float32Array,
  windowSize: number,
  hopSize: number
): Float32Array {
  const numFrames = Math.floor((samples.length - windowSize) / hopSize) + 1;
  const envelope = new Float32Array(numFrames);

  for (let i = 0; i < numFrames; i++) {
    const offset = i * hopSize;
    let energy = 0;
    for (let j = 0; j < windowSize; j++) {
      energy += samples[offset + j] * samples[offset + j];
    }
    envelope[i] = Math.sqrt(energy / windowSize);
  }

  return envelope;
}


function estimateBPM(beats: number[], minBPM: number, maxBPM: number): number {
  if (beats.length < 3) return 120; // default

  // Calculate intervals between consecutive beats
  const intervals: number[] = [];
  for (let i = 1; i < beats.length; i++) {
    intervals.push(beats[i] - beats[i - 1]);
  }

  // Median interval
  intervals.sort((a, b) => a - b);
  const medianInterval = intervals[Math.floor(intervals.length / 2)];

  let bpm = 60 / medianInterval;

  // Adjust to expected range (handle half-time/double-time)
  while (bpm < minBPM) bpm *= 2;
  while (bpm > maxBPM) bpm /= 2;

  return Math.round(bpm);
}
